import './entity-service.professional-caregiver.screen.scss';
import {useState, createRef, useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../i18n/translations';
import {useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {
  Radio,
  FormControlLabel,
  RadioGroup,
  FormLabel,
  FormControl,
  Box,
  Typography,
  Divider,
  Alert,
  Stack,
} from '@mui/material';
import {EntityService} from '../../../../../../backend/interfaces/entity-service';
import {EntityServiceType} from '../../../../../../backend/enums/entity-service-type';
import {ContentPage} from '../../../../../../components/content-page/content-page';
import {
  MarkdownEditor,
  MarkdownEditorConfiguration,
} from '../../../../../../components/markdown-editor/markdown-editor';
import {SmoothImage} from '../../../../../../components/smooth-image/smooth-image';
import {TabletButton} from '../../../../../../components/tablet-button/tablet-button';
import {
  SubmitButton,
  SubmitButtonState,
} from '../../../../../../components/submit-button/submit-button';
import {appColors} from '../../../../../../utils/constants/colors';
import {EntityServicesRepository} from '../../../../../../repositories/entity-services';
import {parsePhoneNumber, PhoneNumber} from 'libphonenumber-js/max';
import {IconPicker} from '../../../../../../components/icon-picker/icon-picker';
import {IconsCatalog} from '../../../../../../backend/enums/icons-catalog';
import {StorageRequestRepository} from '../../../../../../repositories/storage-request';
import {
  EntityServiceDocumentStorageRequestData,
  EntityServiceImageStorageRequestData,
  StorageRequestResult,
} from '../../../../../../backend/interfaces/storage-request';
import {StorageFileType} from '../../../../../../backend/enums/storage-file-type';
import {StorageType} from '../../../../../../backend/enums/storage-type';
import {useTheme} from '@mui/material/styles';
import {AppContextType} from '../../../../../../contexts/app/types';
import {AppContext} from '../../../../../../contexts/app/context';
import {ImageCropperDialog} from '../../../../../../components/image-cropper-dialog/image-cropper-dialog';
import {appSizes} from '../../../../../../utils/constants/sizes';
import {RouterPrompt} from '../../../../../../components/router-prompt/router-prompt';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {PutEntityService} from '../../../../../../backend/requests/put-entity-service';
import {PostEntityService} from '../../../../../../backend/requests/post-entity-service';
import {PostStorageRequest} from '../../../../../../backend/requests/post-storage-request';
import {LoadingContent} from '../../../../../../components/loading-content/loading-content';
import {PhoneNumberInput} from '../../../../../../components/phone-number-input/phone-number-input';
import FlatInput from '../../../../../../components/flat-input/flat-input';
import FlatSelectInput from '../../../../../../components/flat-select-input/flat-select-input';
import {Modal} from '../../../../../../components/modal/modal';
import Button from '@mui/material/Button';
import {LoadingButton} from '@mui/lab';
import EditIcon from '@mui/icons-material/Edit';
import Fab from '@mui/material/Fab';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {Tooltip} from '../../../../../../components/tooltip/tooltip';
import GenericHeader from '../../../../../../components/generic-header/generic-header';

// Hold variables
const availableDisplayOrderValues: Array<number> = [];
const markdownEditorConfiguration: MarkdownEditorConfiguration = {
  inlineStyleControls: {
    bold: true,
  },
  blockStyleControls: {
    ul: true,
    h1: true,
    h2: true,
  },
};

export default function EntityServiceProfessionalCaregiverOfficeScreen({
  canWrite,
}: {
  canWrite: boolean;
}) {
  // Get params
  let {id, entityId}: {id?: string; entityId?: string} = useParams();
  if (id?.toLowerCase() === 'create') {
    id = undefined;
  }

  // Get context
  const appContext: AppContextType = useContext(AppContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Get theme
  const theme = useTheme();

  // Translations
  const {t} = useTranslation();

  // Get history
  let history = useHistory();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Hold state
  let modelInitialState: EntityService | undefined;
  if (!id) {
    // Create
    modelInitialState = {
      entityId: entityId || '',
      type: EntityServiceType.CallGeneralNumber,
      title: '',
      isActive: true,
      phoneNumber: '',
      displayOrder: 1,
      markdownDescription: '',
      markdownDetails: '',
      iconUrl: '',
      imageUrl: '',
      attachedDocumentInformativeNote: '',
    };
  }
  const [model, setModel] = useState<EntityService | undefined>(modelInitialState);
  const [originalModel, setOriginalModel] = useState<EntityService | undefined>(modelInitialState);
  const [countryCode, setCountryCode] = useState<{
    phone: string;
    code: string;
  }>({
    phone: '+351',
    code: 'PT',
  });
  const [selectedDocumentFile, setSelectedDocumentFile] = useState<string | undefined>();
  const [selectedImageFile, setSelectedImageFile] = useState<File | undefined>();
  const [iconPickerOpened, setIconPickerOpened] = useState<boolean>(false);
  const [saveDialogOpened, setSaveDialogOpened] = useState<boolean>(false);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);
  const [imageCropperFile, setImageCropperFile] = useState<File | undefined>();

  // Buttons
  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(false);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(false);
  const [saveButtonState, setSaveButtonState] = useState<SubmitButtonState>(
    SubmitButtonState.Normal
  );
  const [deleteButtonState, setDeleteButtonState] = useState<SubmitButtonState>(
    SubmitButtonState.Normal
  );

  // Form validation
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [titleError, setTitleError] = useState<string | undefined>();
  const [showTitleError, setShowTitleError] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>();
  const [showPhoneNumberError, setShowPhoneNumberError] = useState<boolean>(false);
  const [markdownDescriptionError, setMarkdownDescriptionError] = useState<string | undefined>();
  const [showMarkdownDescriptionError, setShowMarkdownDescriptionError] = useState<boolean>(false);
  const [markdownDetailsError, setMarkdownDetailsError] = useState<string | undefined>();
  const [showMarkdownDetailsError, setShowMarkdownDetailsError] = useState<boolean>(false);
  const [iconUrlError, setIconUrlError] = useState<string | undefined>();
  const [showIconUrlError, setShowIconUrlError] = useState<boolean>(false);
  const [imageUrlError, setImageUrlError] = useState<string | undefined>();
  const [showImageUrlError, setShowImageUrlError] = useState<boolean>(false);

  // Refs
  const imageInputFileRef = createRef<any>();

  // Variables
  if (availableDisplayOrderValues.length === 0) {
    for (let i = 1; i <= 100; i++) {
      availableDisplayOrderValues.push(i);
    }
  }

  // Set the model
  useEffect(() => {
    const init = async () => {
      // Init some state
      setDataChanged(false);
      setSelectedImageFile(undefined);
      setSelectedDocumentFile(undefined);
      setSaveButtonState(SubmitButtonState.Normal);
      setDeleteButtonState(SubmitButtonState.Normal);

      if (id) {
        try {
          // Get model
          const modelWithId: EntityService = await EntityServicesRepository.instance.getById(id);

          // Evaluate
          if (modelWithId) {
            // Note: The original model will have the complete phone number (ex: "+351910000000").
            // The model will only have the phone number without the country calling code.

            // Map phone number
            let parsedPhoneNumber: PhoneNumber | undefined;
            if (modelWithId.phoneNumber) {
              try {
                // Parse
                parsedPhoneNumber = parsePhoneNumber(modelWithId.phoneNumber);
              } catch (error) {
                // Log
                console.debug('🔴 Could not parse entity service phone number.', error);
              }
            }

            // Model
            const entityServiceModel: EntityService = {
              id,
              entityId: modelWithId.entityId,
              title: modelWithId.title,
              isActive: modelWithId.isActive,
              type: modelWithId.type,
              phoneNumber:
                parsedPhoneNumber && parsedPhoneNumber.isValid()
                  ? parsedPhoneNumber.nationalNumber.toString()
                  : undefined,
              displayOrder: modelWithId.displayOrder,
              markdownDescription: modelWithId.markdownDescription,
              markdownDetails: modelWithId.markdownDetails,
              attachedDocumentUrl: modelWithId.attachedDocumentUrl,
              attachedDocumentInformativeNote: modelWithId.attachedDocumentInformativeNote,
              iconUrl: modelWithId.iconUrl,
              imageUrl: modelWithId.imageUrl,
            };

            // Update models
            setOriginalModel({
              ...entityServiceModel,
              phoneNumber: modelWithId.phoneNumber,
            });
            setModel(entityServiceModel);

            // Country code
            if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
              // Update
              setCountryCode({
                phone: `+${parsedPhoneNumber.countryCallingCode.toString()}`,
                code: parsedPhoneNumber.country?.toString() || '',
              });
            }
          } else {
            // Error
            throw new Error('Entity service is empty.');
          }
        } catch (error) {
          // Log
          console.error('🔴 Could not get the entity services.', error);

          // Error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.entity_services.edit.errors.service_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace(`/${id}`, '')}`);
        }
      }
    };

    // Init
    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Form validation
  useEffect(() => {
    if (originalModel && model) {
      // ----- Fields validation -----
      // Holds validation
      let titleValid: boolean = false;
      let phoneNumberValid: boolean = false;
      let markdownDescriptionValid: boolean = false;
      let markdownDetailsValid: boolean = false;
      let iconUrlValid: boolean = false;
      let imageUrlValid: boolean = false;

      // Title validation
      if (model.title && model.title.trim().length > 0) {
        // No errors
        if (titleError !== undefined) {
          setTitleError(undefined);
        }

        // Valid
        titleValid = true;
      } else {
        // Errors
        if (showTitleError) {
          setTitleError(
            id
              ? t(translations.entity_services.edit.errors.edit_form_validation_invalid_field)
              : t(translations.entity_services.edit.errors.create_form_validation_invalid_field)
          );
        }

        // Invalid
        titleValid = false;
      }

      // Phone number validation
      if (model.type === EntityServiceType.CallSpecificNumber) {
        if (model.phoneNumber && model.phoneNumber.trim().length > 0) {
          try {
            // Parse
            const parsedPhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
              `${countryCode.phone}${model.phoneNumber.trim()}`
            );

            if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
              // No errors
              if (phoneNumberError !== undefined) {
                setPhoneNumberError(undefined);
              }

              // Valid
              phoneNumberValid = true;
            } else {
              // Errors
              if (showPhoneNumberError) {
                setPhoneNumberError(
                  t(translations.entity_services.edit.errors.form_validation_invalid_phone_number)
                );
              }

              // Invalid
              phoneNumberValid = false;
            }
          } catch (error) {
            // Errors
            if (showPhoneNumberError) {
              setPhoneNumberError(
                t(translations.entity_services.edit.errors.form_validation_invalid_phone_number)
              );
            }

            // Invalid
            phoneNumberValid = false;
          }
        } else {
          // Errors
          if (showPhoneNumberError) {
            setPhoneNumberError(
              id
                ? t(translations.entity_services.edit.errors.edit_form_validation_invalid_field)
                : t(translations.entity_services.edit.errors.create_form_validation_invalid_field)
            );
          }

          // Invalid
          phoneNumberValid = false;
        }
      } else {
        // No errors
        if (phoneNumberError !== undefined) {
          setPhoneNumberError(undefined);
        }

        // Valid
        phoneNumberValid = true;
      }

      // Markdown description validation
      if (model.markdownDescription && model.markdownDescription.trim().length > 0) {
        // No errors
        if (markdownDescriptionError !== undefined) {
          setMarkdownDescriptionError(undefined);
        }

        // Valid
        markdownDescriptionValid = true;
      } else {
        // Errors
        if (showMarkdownDescriptionError) {
          setMarkdownDescriptionError(
            id
              ? t(translations.entity_services.edit.errors.edit_form_validation_invalid_field)
              : t(translations.entity_services.edit.errors.create_form_validation_invalid_field)
          );
        }

        // Invalid
        markdownDescriptionValid = false;
      }

      // Markdown details validation
      if (model.markdownDetails && model.markdownDetails.trim().length > 0) {
        // No errors
        if (markdownDetailsError !== undefined) {
          setMarkdownDetailsError(undefined);
        }

        // Valid
        markdownDetailsValid = true;
      } else {
        // Errors
        if (showMarkdownDetailsError) {
          setMarkdownDetailsError(
            id
              ? t(translations.entity_services.edit.errors.edit_form_validation_invalid_field)
              : t(translations.entity_services.edit.errors.create_form_validation_invalid_field)
          );
        }

        // Invalid
        markdownDetailsValid = false;
      }

      // Icon url validation
      if (model.iconUrl && model.iconUrl.trim().length > 0) {
        // No errors
        if (iconUrlError !== undefined) {
          setIconUrlError(undefined);
        }

        // Valid
        iconUrlValid = true;
      } else {
        // Errors
        if (showIconUrlError) {
          setIconUrlError(
            t(translations.entity_services.edit.errors.create_form_validation_invalid_icon)
          );
        }

        // Invalid
        iconUrlValid = false;
      }

      // Image url validation
      if (model.imageUrl && model.imageUrl.trim().length > 0) {
        // No errors
        if (imageUrlError !== undefined) {
          setImageUrlError(undefined);
        }

        // Valid
        imageUrlValid = true;
      } else {
        // Errors
        if (showImageUrlError) {
          setImageUrlError(
            t(translations.entity_services.edit.errors.create_form_validation_invalid_image)
          );
        }

        // Invalid
        imageUrlValid = false;
      }
      // ----- Fields validation -----

      // ----- Data changed -----
      let dataWasUpdated: boolean = false;

      // Validate attachedDocumentInformativeNote
      let attachedDocumentInformativeNoteChanged: boolean = false;
      if (
        originalModel.attachedDocumentInformativeNote !== undefined &&
        model.attachedDocumentInformativeNote?.trim() !==
          originalModel.attachedDocumentInformativeNote?.trim()
      ) {
        attachedDocumentInformativeNoteChanged = true;
      } else if (
        originalModel.attachedDocumentInformativeNote === undefined &&
        model.attachedDocumentInformativeNote !== undefined &&
        model.attachedDocumentInformativeNote?.trim().length !== 0
      ) {
        attachedDocumentInformativeNoteChanged = true;
      }

      // Validate phoneNumber
      let phoneNumberChanged = false;
      if (model.type === EntityServiceType.CallSpecificNumber) {
        if (
          (!originalModel.phoneNumber && model.phoneNumber) ||
          (originalModel.phoneNumber && !model.phoneNumber) ||
          (model.phoneNumber &&
            originalModel.phoneNumber &&
            `${countryCode.phone}${model.phoneNumber?.trim()}` !==
              originalModel.phoneNumber?.trim())
        ) {
          phoneNumberChanged = true;
        }
      }

      if (
        model.title.trim() !== originalModel.title.trim() ||
        model.displayOrder !== originalModel.displayOrder ||
        model.isActive !== originalModel.isActive ||
        model.type !== originalModel.type ||
        phoneNumberChanged ||
        model.markdownDescription.trim() !== originalModel.markdownDescription.trim() ||
        model.markdownDetails.trim() !== originalModel.markdownDetails.trim() ||
        attachedDocumentInformativeNoteChanged ||
        model.iconUrl !== originalModel.iconUrl ||
        model.imageUrl !== originalModel.imageUrl
      ) {
        // Set it
        dataWasUpdated = true;

        // Set it
        setDataChanged(true);
      } else {
        // Set it
        dataWasUpdated = false;

        // Set it
        setDataChanged(false);
      }
      // ----- Data changed -----

      // ----- Submit button validation -----
      if (id) {
        // Edit
        if (
          titleValid &&
          phoneNumberValid &&
          markdownDetailsValid &&
          markdownDescriptionValid &&
          iconUrlValid &&
          imageUrlValid &&
          dataWasUpdated
        ) {
          // Enable save button
          setSaveButtonDisabled(false);
        } else {
          // Disable save button
          setSaveButtonDisabled(true);
        }
      } else {
        // Create
        if (
          titleValid &&
          phoneNumberValid &&
          markdownDetailsValid &&
          markdownDescriptionValid &&
          iconUrlValid &&
          imageUrlValid
        ) {
          // Enable save button
          setSaveButtonDisabled(false);
        } else {
          // Disable save button
          setSaveButtonDisabled(true);
        }
      }
      // ----- Submit button validation -----
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    originalModel,
    model,
    dataChanged,
    countryCode,
    showTitleError,
    showMarkdownDescriptionError,
    showMarkdownDetailsError,
    showPhoneNumberError,
    showImageUrlError,
    showIconUrlError,
    titleError,
    phoneNumberError,
    markdownDescriptionError,
    markdownDetailsError,
    iconUrlError,
    imageUrlError,
  ]);

  // Methods
  // const onDocumentFileChange = (event: any) => {
  //     // Update the state
  //     setSelectedDocumentFile(event.target.files[0]);
  //     if (model) {
  //         setModel({
  //             ...model,
  //             attachedDocumentUrl: event.target.files[0].name,
  //         });
  //     }
  // };

  const handleChangeImageFile = (event: any) => {
    // Evaluate change
    if (event.target.files[0]) {
      // Open image cropper dialog
      setImageCropperFile(event.target.files[0]);
    }
  };

  const handleImageFileCropperDialogClosed = (result?: File) => {
    // Close image cropper dialog
    setImageCropperFile(undefined);

    if (result) {
      // Update the state
      setSelectedImageFile(result);

      // Initialize a new file reader
      const reader = new FileReader();

      // On reader load
      reader.onload = () => {
        // Link result to display element
        setModel({
          ...model,
          imageUrl: reader.result as string, // base64
        } as EntityService);
      };

      // Launch reading process
      reader.readAsDataURL(result);
    }
  };

  const handleSubmit = async () => {
    // Not expected
    if (!model || !originalModel) {
      return;
    }

    // Set submit button state
    setSaveButtonState(SubmitButtonState.Loading);

    // Disable delete button
    setDeleteButtonDisabled(true);

    // Process phone number
    let phoneNumber: string | undefined;
    if (model.type === EntityServiceType.CallSpecificNumber) {
      try {
        // Parse
        const parsedPhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
          `${countryCode.phone}${model.phoneNumber?.trim()}`
        );

        // Evaluate
        if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
          // Valid
          phoneNumber = parsedPhoneNumber.number.toString();
        } else {
          // Error
          throw new Error('Phone number parse failed');
        }
      } catch (error) {
        // Error
        console.debug('Could not process phone number.', error);
      }
    }

    // Save
    try {
      // Document file upload
      let uploadedDocumentLinkingUrl: string | undefined;
      if (selectedDocumentFile) {
        // Request body
        const requestBodyDocumentFile: PostStorageRequest<EntityServiceDocumentStorageRequestData> =
          {
            type: StorageType.EntityServiceDocument,
            fileType: StorageFileType.PDF,
            data: {
              entityId: model.entityId,
            },
          };

        // Some file was selected to be upload, request upload to the backend
        const storageRequestResult: StorageRequestResult<EntityServiceDocumentStorageRequestData> =
          await StorageRequestRepository.instance.create<EntityServiceDocumentStorageRequestData>(
            requestBodyDocumentFile
          );

        // Upload file
        uploadedDocumentLinkingUrl =
          await StorageRequestRepository.instance.upload<EntityServiceDocumentStorageRequestData>(
            storageRequestResult,
            selectedDocumentFile
          );
      }

      // Image file upload
      let uploadedImageLinkingUrl: string | undefined;
      if (selectedImageFile) {
        // Request body
        const requestBodyImageFile: PostStorageRequest<EntityServiceImageStorageRequestData> = {
          type: StorageType.EntityServiceImage,
          fileType: StorageFileType.JPG,
          data: {
            entityId: model.entityId,
          },
        };

        // Some file was selected to be upload, request upload to the backend
        const storageRequestResult: StorageRequestResult<EntityServiceImageStorageRequestData> =
          await StorageRequestRepository.instance.create<EntityServiceImageStorageRequestData>(
            requestBodyImageFile
          );

        // Upload file
        uploadedImageLinkingUrl =
          await StorageRequestRepository.instance.upload<EntityServiceImageStorageRequestData>(
            storageRequestResult,
            selectedImageFile
          );
      }

      // Evaluate action type
      if (id) {
        // Attached document url
        let attachedDocumentUrl: string | null = originalModel.attachedDocumentUrl || null;
        if (selectedDocumentFile && uploadedDocumentLinkingUrl) {
          attachedDocumentUrl = uploadedDocumentLinkingUrl;
        } else if (originalModel.attachedDocumentUrl && !model.attachedDocumentUrl) {
          attachedDocumentUrl = null;
        }

        // Request body
        // Note: For PUT request, we use "null" to remove a field. The backend will ignore any field set to "undefined".
        const requestBody: PutEntityService = {
          entityId: model.entityId,
          type: model.type,
          title: model.title.trim(),
          isActive: model.isActive,
          displayOrder: model.displayOrder,
          markdownDescription: model.markdownDescription.trim(),
          markdownDetails: model.markdownDetails.trim(),
          iconUrl: model.iconUrl,
          imageUrl:
            selectedImageFile && uploadedImageLinkingUrl
              ? uploadedImageLinkingUrl
              : originalModel.imageUrl,
          phoneNumber: phoneNumber || null,
          attachedDocumentUrl: attachedDocumentUrl,
          attachedDocumentInformativeNote: model.attachedDocumentInformativeNote
            ? model.attachedDocumentInformativeNote.trim()
            : null,
        };

        // Request update to the backend
        const result: EntityService = await EntityServicesRepository.instance.updateById(
          id,
          requestBody
        );

        // Reset models
        setModel({
          ...result,
          phoneNumber: phoneNumber ? model.phoneNumber : undefined,
        });
        setOriginalModel(result);

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.entity_services.edit.success.edit_success_message),
        });

        // Set submit button state
        setSaveButtonState(SubmitButtonState.Success);

        // Enable delete button
        setDeleteButtonDisabled(false);
      } else {
        // Request body
        const requestBody: PostEntityService = {
          entityId: model.entityId,
          type: model.type,
          title: model.title.trim(),
          isActive: model.isActive,
          displayOrder: model.displayOrder,
          markdownDescription: model.markdownDescription.trim(),
          markdownDetails: model.markdownDetails.trim(),
          iconUrl: model.iconUrl,
          imageUrl: selectedImageFile && uploadedImageLinkingUrl ? uploadedImageLinkingUrl : '',
          phoneNumber,
          attachedDocumentUrl:
            selectedDocumentFile && uploadedDocumentLinkingUrl
              ? uploadedDocumentLinkingUrl
              : undefined,
          attachedDocumentInformativeNote: model.attachedDocumentInformativeNote
            ? model.attachedDocumentInformativeNote.trim()
            : undefined,
        };

        // Request creation to the backend
        const result: EntityService = await EntityServicesRepository.instance.create(requestBody);

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Reset navigation to edit mode
          history.replace(url.replace('/create', `/${result.id}`));

          // Reset models
          setModel({
            ...result,
            phoneNumber: phoneNumber ? model.phoneNumber : undefined,
          });
          setOriginalModel(result);

          // Show toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.entity_services.edit.success.create_success_message, {
              name: model.title,
            }),
          });

          // Set submit button state
          setSaveButtonState(SubmitButtonState.Success);

          // Enable delete button
          setDeleteButtonDisabled(false);
        }, 500);
      }
    } catch (error) {
      // Error
      console.debug('🔴 Could not create/edit entity service.', error);

      // Set submit button state
      setSaveButtonState(SubmitButtonState.Error);

      // Enable delete button
      setDeleteButtonDisabled(false);
    }
  };

  const handleSaveChanges = (event: any) => {
    event.preventDefault();

    // Not expected
    if (!model || !originalModel) {
      return;
    }

    // Show Confirm
    setSaveDialogOpened(true);
  };

  const handleSaveDialogClosed = async (reason?: string) => {
    // Close
    setSaveDialogOpened(false);

    // Evaluate decision
    if (reason === 'yes') {
      // Start save process
      handleSubmit();
    }
  };

  const handleDeleteDialogClosed = async (reason?: string) => {
    // Close
    setDeleteDialogOpened(false);

    // Evaluate decision
    if (reason === 'yes') {
      // Disable submit button
      setSaveButtonDisabled(true);

      // Set delete button state
      setDeleteButtonState(SubmitButtonState.Loading);

      try {
        if (id) {
          // Delete entity service
          await EntityServicesRepository.instance.deleteById(id);
        } else {
          // Error
          throw new Error('Invalid values to delete the entity service.');
        }

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Navigate back
          history.replace(
            url.replace(
              `/${id}`,
              `/?${ScreenPaths.EntityServicesDeletedQuery}=${originalModel?.title}`
            )
          );
        }, 500);
      } catch (error) {
        // Error
        console.debug('🔴 Could not delete entity service.', error);

        // Set delete button state
        setDeleteButtonState(SubmitButtonState.Error);

        // Enable submit button
        setSaveButtonDisabled(false);
      }
    }
  };

  // Exit
  const handleExit = () => {
    // Go to the entity services list
    history.push(id ? `${url.replace(`/${id}`, '')}` : `${url.replace('/create', '')}`);
  };

  // ----- Handle blur ------

  const handleBlurTitle = () => {
    if (!showTitleError) {
      setShowTitleError(true);
    }
  };

  const handleBlurPhoneNumber = () => {
    if (!showPhoneNumberError) {
      setShowPhoneNumberError(true);
    }
  };

  const handleBlurMarkdownDescription = () => {
    if (!showMarkdownDescriptionError) {
      setShowMarkdownDescriptionError(true);
    }
  };

  const handleBlurMarkdownDetails = () => {
    if (!showMarkdownDetailsError) {
      setShowMarkdownDetailsError(true);
    }
  };

  const handleBlurIconUrl = () => {
    if (!showIconUrlError) {
      setShowIconUrlError(true);
    }
  };

  const handleBlurImageUrl = () => {
    if (!showImageUrlError) {
      setShowImageUrlError(true);
    }
  };

  // ----- Handle blur ------

  // ----- Handle change ------

  const handleChangeTitle = (value: string | undefined | null) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setModel({
      ...model,
      title: finalValue,
    } as EntityService);
  };

  // ----- Handle change ------

  const inputDisabledTooltip = !canWrite
    ? t(translations.shared.input_disabled_tooltip)
    : undefined;
  const pageTitle = id
    ? canWrite
      ? t(translations.entity_services.edit.title_edit)
      : t(translations.entity_services.edit.title_view)
    : t(translations.entity_services.edit.title_create);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`,
          },
          {
            label: t(translations.screen_paths.institution_settings_services),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Services}`,
          },
          {
            label: pageTitle,
            link: url,
          },
        ]}
        isLoading={false}
        leftColumn={<></>}
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <ContentPage>
            {model ? (
              <form onSubmit={handleSaveChanges}>
                <Box className="entity-service-line-1-box" sx={{marginTop: 0}}>
                  <Box className="entity-service-line-1-left">
                    <FlatInput
                      id="entity-service-title"
                      label={t(translations.entity_services.edit.title_input)}
                      value={model.title || ''}
                      onChange={(event) => handleChangeTitle(event.target.value)}
                      onBlur={handleBlurTitle}
                      disabled={
                        saveButtonState === SubmitButtonState.Loading ||
                        deleteButtonState === SubmitButtonState.Loading ||
                        !canWrite
                      }
                      disabledTooltip={inputDisabledTooltip}
                      required={canWrite}
                      errors={titleError}
                      type="text"
                    />
                  </Box>
                  <Box className="entity-service-line-1-right">
                    <FlatSelectInput
                      id="entity-service-state-select"
                      label={t(translations.entity_services.edit.state_input)}
                      value={model.isActive.toString()}
                      onChange={(event) => {
                        // Set it
                        setModel({
                          ...model,
                          isActive: JSON.parse(event.target.value as string),
                        });
                      }}
                      disabled={
                        saveButtonState === SubmitButtonState.Loading ||
                        deleteButtonState === SubmitButtonState.Loading ||
                        !canWrite
                      }
                      disabledTooltip={inputDisabledTooltip}
                      required={canWrite}
                      options={[
                        {
                          value: 'true',
                          label: t(translations.entity_services.edit.state_value_true),
                        },
                        {
                          value: 'false',
                          label: t(translations.entity_services.edit.state_value_false),
                        },
                      ]}
                    />
                  </Box>
                </Box>

                <Box className="entity-service-line-1-box">
                  <Box className="entity-service-line-1-left">
                    <Tooltip.NotAllowedIf
                      condition={!canWrite}
                      title={inputDisabledTooltip}
                      sx={{display: 'block', width: '100%'}}
                    >
                      <FormControl component="fieldset">
                        <FormLabel
                          component="legend"
                          color="neutral"
                          error={phoneNumberError !== undefined}
                          disabled={
                            saveButtonState === SubmitButtonState.Loading ||
                            deleteButtonState === SubmitButtonState.Loading ||
                            !canWrite
                          }
                        >
                          {`${t(translations.entity_services.edit.phone_number_input)}${
                            canWrite ? ' *' : ''
                          }`}
                        </FormLabel>
                        <RadioGroup
                          value={model.type}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            // Set it
                            setModel({
                              ...model,
                              type: event.target.value as EntityServiceType,
                            });
                          }}
                          name="entity-service-type-radio-buttons-group"
                          row
                        >
                          <FormControlLabel
                            value={EntityServiceType.CallGeneralNumber}
                            control={
                              <Radio
                                disabled={
                                  saveButtonState === SubmitButtonState.Loading ||
                                  deleteButtonState === SubmitButtonState.Loading
                                }
                              />
                            }
                            label={t(
                              translations.entity_services.edit.phone_number_use_general_number
                            )}
                            disabled={!canWrite}
                          />
                          <FormControlLabel
                            value={EntityServiceType.CallSpecificNumber}
                            control={
                              <Radio
                                disabled={
                                  saveButtonState === SubmitButtonState.Loading ||
                                  deleteButtonState === SubmitButtonState.Loading
                                }
                              />
                            }
                            label={t(
                              translations.entity_services.edit.phone_number_use_specific_number
                            )}
                            disabled={!canWrite}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Tooltip.NotAllowedIf>

                    {model.type === EntityServiceType.CallSpecificNumber ? (
                      <PhoneNumberInput
                        id="entity-service-specific-phone-number-value"
                        className="entity-service-specific-phone-number-box"
                        label={t(translations.entity_services.edit.number_input)}
                        countryCode={countryCode}
                        phoneNumber={model.phoneNumber || ''}
                        disabled={
                          saveButtonState === SubmitButtonState.Loading ||
                          deleteButtonState === SubmitButtonState.Loading ||
                          !canWrite
                        }
                        disabledTooltip={inputDisabledTooltip}
                        handleChangeCountryCode={(value) => {
                          // Set it
                          setCountryCode(value);
                        }}
                        handleChangePhoneNumber={(value) => {
                          // Set it
                          setModel({
                            ...model,
                            phoneNumber: value,
                          });
                        }}
                        handleBlur={handleBlurPhoneNumber}
                        required={true}
                        error={phoneNumberError}
                      />
                    ) : undefined}
                  </Box>

                  <Box className="entity-service-line-1-right">
                    <FlatSelectInput
                      id="entity-service-display-order-select-label"
                      label={t(translations.entity_services.edit.display_order_input)}
                      value={model.displayOrder}
                      onChange={(event) => {
                        // Set it
                        setModel({
                          ...model,
                          displayOrder: Number(event.target.value),
                        });
                      }}
                      disabled={
                        saveButtonState === SubmitButtonState.Loading ||
                        deleteButtonState === SubmitButtonState.Loading ||
                        !canWrite
                      }
                      disabledTooltip={inputDisabledTooltip}
                      required={canWrite}
                      options={availableDisplayOrderValues.map((value) => ({
                        value,
                        label: value.toString(),
                      }))}
                    />
                  </Box>
                </Box>
                <Box className="entity-service-markdown-editor-box">
                  <MarkdownEditor
                    label={t(translations.entity_services.edit.description_input)}
                    disabledTooltip={inputDisabledTooltip}
                    initialValue={model.markdownDescription}
                    onChange={(newValue: string) => {
                      // Set it
                      setModel({
                        ...model,
                        markdownDescription: newValue,
                      });
                    }}
                    onBlur={handleBlurMarkdownDescription}
                    invalid={markdownDescriptionError ? true : false}
                    helperText={markdownDescriptionError || undefined}
                    disabled={
                      saveButtonState === SubmitButtonState.Loading ||
                      deleteButtonState === SubmitButtonState.Loading ||
                      !canWrite
                    }
                    required={true}
                    configuration={markdownEditorConfiguration}
                  />
                </Box>
                <Box className="entity-service-markdown-editor-box">
                  <MarkdownEditor
                    label={t(translations.entity_services.edit.details_input)}
                    disabledTooltip={inputDisabledTooltip}
                    initialValue={model.markdownDetails}
                    onChange={(newValue: string) => {
                      // Set it
                      setModel({
                        ...model,
                        markdownDetails: newValue,
                      });
                    }}
                    onBlur={handleBlurMarkdownDetails}
                    invalid={markdownDetailsError ? true : false}
                    helperText={markdownDetailsError || undefined}
                    disabled={
                      saveButtonState === SubmitButtonState.Loading ||
                      deleteButtonState === SubmitButtonState.Loading ||
                      !canWrite
                    }
                    required={true}
                    minHeight={300}
                    configuration={markdownEditorConfiguration}
                  />
                </Box>
                {/* <Box sx={{ marginTop: '32px' }}>
                        <FormLabel component="legend">
                            {t(translations.entityServiceOfficeScreen.attachDocument)}
                        </FormLabel>
                        <Box className="entity-service-attach-document-box">
                            <TextField
                                className="entity-service-attach-document-box-text-field"
                                sx={{ flex: 1 }}
                                size="small"
                                disabled={true}
                                id="entity-service-attach-document"
                                value={
                                    model.attachedDocumentUrl && model.attachedDocumentUrl !== ''
                                        ? model.attachedDocumentUrl.substring(
                                                model.attachedDocumentUrl.lastIndexOf('/')
                                            )
                                        : ''
                                }
                            />
                            {!selectedDocumentFile && !model.attachedDocumentUrl ? (
                                <label
                                    htmlFor="entity-service-attach-document-button-file"
                                    className="entity-service-attach-document-box-actions"
                                >
                                    <input
                                        disabled={formLocked}
                                        style={{ display: 'none' }}
                                        accept="application/pdf"
                                        id="entity-service-attach-document-button-file"
                                        type="file"
                                        onChange={onDocumentFileChange}
                                    />
                                    <Button
                                        className="entity-service-attach-document-box-action"
                                        disabled={formLocked}
                                        color="primary"
                                        variant="contained"
                                        component="span"
                                        endIcon={<AttachFileIcon />}
                                    >
                                        {t(translations.entityServiceOfficeScreen.upload)}
                                    </Button>
                                </label>
                            ) : (
                                <Box className="entity-service-attach-document-box-actions">
                                    <Button
                                        className="entity-service-attach-document-box-action"
                                        disabled={formLocked}
                                        color="error"
                                        variant="contained"
                                        component="span"
                                        endIcon={<LinkOffIcon />}
                                        onClick={(event: any) => {
                                            event.preventDefault();
                                            setSelectedDocumentFile(undefined);
                                            setFormIsPristine(false);
                                            setModel({
                                                ...model,
                                                attachedDocumentUrl: undefined,
                                            });
                                        }}
                                    >
                                        {t(translations.entityServiceOfficeScreen.removeDocument)}
                                    </Button>
                                    {model.attachedDocumentUrl &&
                                    model.attachedDocumentUrl.indexOf('http') === 0 ? (
                                        <Button
                                            className="entity-service-attach-document-box-action"
                                            color="primary"
                                            variant="contained"
                                            component={Link}
                                            endIcon={<VisibilityIcon />}
                                            to={{ pathname: model.attachedDocumentUrl }}
                                            target="_blank"
                                        >
                                            {t(translations.entityServiceOfficeScreen.viewDocument)}
                                        </Button>
                                    ) : undefined}
                                </Box>
                            )}
                        </Box>
                    </Box> */}
                <Box className="entity-service-informative-note-box">
                  <FlatInput
                    id="entity-service-document-informative-note"
                    className="entity-service-informative-note-box-text-input"
                    label={t(translations.entity_services.edit.document_informative_note_input)}
                    disabledTooltip={inputDisabledTooltip}
                    value={model.attachedDocumentInformativeNote || ''}
                    onChange={(event) => {
                      // Set it
                      setModel({
                        ...model,
                        attachedDocumentInformativeNote: event.target.value,
                      });
                    }}
                    disabled={
                      saveButtonState === SubmitButtonState.Loading ||
                      deleteButtonState === SubmitButtonState.Loading ||
                      !canWrite
                    }
                    multiline={true}
                    rows={2}
                    type="text"
                  />
                </Box>
                <Box className="entity-service-graphics-box">
                  <Box
                    className="entity-service-graphics-box-smooth-image-icon"
                    sx={{
                      maxWidth: `${appSizes.systemButtonSize.iconContainerEnabledSize.width}px`,
                      width: '100%',
                    }}
                  >
                    <FormLabel
                      component="legend"
                      className="entity-service-graphics-box-icon-label"
                      error={iconUrlError !== undefined}
                      disabled={
                        saveButtonState === SubmitButtonState.Loading ||
                        deleteButtonState === SubmitButtonState.Loading ||
                        !canWrite
                      }
                    >
                      {`${t(translations.entity_services.edit.icon_input)}${canWrite ? ' *' : ''}`}
                    </FormLabel>
                    <SmoothImage
                      src={model.iconUrl}
                      width={appSizes.systemButtonSize.iconContainerEnabledSize.width}
                      height={appSizes.systemButtonSize.iconContainerEnabledSize.height}
                      imageWidth={appSizes.systemButtonSize.iconEnabledSize.width}
                      imageHeight={appSizes.systemButtonSize.iconEnabledSize.height}
                      borderRadius={appSizes.systemButtonSize.innerCircleEnabledSize.borderRadius}
                      backgroundColor={appColors.appButtons.entityServices.enabled}
                      borderWidth={iconUrlError ? 2 : undefined}
                      borderColor={iconUrlError ? theme.palette.error.main : undefined}
                    />
                    {iconUrlError && (
                      <Box className="entity-service-graphics-box-icon-error-box">
                        <Typography variant="caption" color="error">
                          {iconUrlError}
                        </Typography>
                      </Box>
                    )}
                    <Box className="entity-service-graphics-box-icon-edit-button-box">
                      <Fab
                        disabled={
                          saveButtonState === SubmitButtonState.Loading ||
                          deleteButtonState === SubmitButtonState.Loading ||
                          !canWrite
                        }
                        size="small"
                        color="primary"
                        onClick={() => {
                          // Handle blur
                          handleBlurIconUrl();

                          // Open picker
                          setIconPickerOpened(true);
                        }}
                      >
                        <EditIcon />
                      </Fab>
                    </Box>
                  </Box>
                  <Box
                    className="entity-service-graphics-box-image-box"
                    sx={{
                      maxWidth: `${appSizes.appButtonSize.width}px`,
                      width: '100%',
                    }}
                  >
                    <FormLabel
                      component="legend"
                      className="entity-service-graphics-box-image-box-label"
                      error={imageUrlError !== undefined}
                      disabled={
                        saveButtonState === SubmitButtonState.Loading ||
                        deleteButtonState === SubmitButtonState.Loading ||
                        !canWrite
                      }
                    >
                      {`${t(translations.entity_services.edit.image_input)}${canWrite ? ' *' : ''}`}
                    </FormLabel>
                    <SmoothImage
                      src={model.imageUrl}
                      width={appSizes.appButtonSize.width}
                      height={appSizes.appButtonSize.height}
                      imageWidth={appSizes.appButtonSize.width}
                      imageHeight={appSizes.appButtonSize.height}
                      borderRadius={appSizes.appButtonSize.borderRadius}
                      borderWidth={imageUrlError ? 2 : undefined}
                      borderColor={imageUrlError ? theme.palette.error.main : undefined}
                    />
                    <Box className="entity-service-graphics-box-image-box-alert-box">
                      <Alert severity="info">
                        {t(translations.entity_services.edit.image_info)}
                      </Alert>
                    </Box>
                    {imageUrlError && (
                      <Box className="entity-service-graphics-box-image-box-error-box">
                        <Typography variant="caption" color="error">
                          {imageUrlError}
                        </Typography>
                      </Box>
                    )}
                    <Box className="entity-service-graphics-box-image-box-edit-button-box">
                      <input
                        ref={imageInputFileRef}
                        disabled={
                          saveButtonState === SubmitButtonState.Loading ||
                          deleteButtonState === SubmitButtonState.Loading
                        }
                        style={{display: 'none'}}
                        accept="image/*"
                        id="entity-service-image-file-button"
                        type="file"
                        onChange={handleChangeImageFile}
                        value=""
                      />
                      <Fab
                        disabled={
                          saveButtonState === SubmitButtonState.Loading ||
                          deleteButtonState === SubmitButtonState.Loading ||
                          !canWrite
                        }
                        size="small"
                        color="primary"
                        onClick={() => {
                          // Open
                          imageInputFileRef.current.click();

                          // Handle blur
                          handleBlurImageUrl();
                        }}
                      >
                        <EditIcon />
                      </Fab>
                    </Box>
                  </Box>
                  <Box className="entity-service-graphics-box-button-preview-box">
                    <Box>
                      <FormLabel component="legend" disabled={!canWrite}>
                        {t(translations.entity_services.edit.button_preview)}
                      </FormLabel>
                      <Box className="entity-service-graphics-box-button-preview-box-tablet-button-box">
                        <TabletButton
                          imageUrl={model.imageUrl}
                          iconUrl={model.iconUrl}
                          title={model.title}
                          appButtonColors={appColors.appButtons.entityServices}
                          emptyTitle={t(translations.entity_services.edit.title_input)}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {model.id ? (
                  <Box className="entity-service-delete-box">
                    <Divider />
                    <Stack
                      className="entity-service-actions-footer"
                      flexDirection="row"
                      gap="10px"
                      justifyContent="space-between"
                    >
                      <Button
                        variant="text"
                        color="error"
                        type="button"
                        sx={{
                          width: '100%',
                          height: '40px',
                          padding: '8px 16px',
                          borderRadius: '5px',
                          maxWidth: '252px',
                        }}
                        disabled={
                          saveButtonState === SubmitButtonState.Loading ||
                          deleteButtonState === SubmitButtonState.Loading
                        }
                        onClick={handleExit}
                      >
                        {t(translations.office.generics.cancel)}
                      </Button>
                      <SubmitButton
                        disabled={
                          !canWrite ||
                          deleteButtonDisabled ||
                          saveButtonState === SubmitButtonState.Loading ||
                          deleteButtonState === SubmitButtonState.Loading
                        }
                        state={deleteButtonState}
                        onStateChange={(newState: SubmitButtonState) => {
                          setDeleteButtonState(newState);
                        }}
                        normalStateLabel={t(translations.entity_services.edit.delete_button)}
                        loadingStateLabel={t(translations.entity_services.edit.deleting_button)}
                        successStateLabel={t(
                          translations.entity_services.edit.success.delete_success_button
                        )}
                        errorStateLabel={t(
                          translations.entity_services.edit.errors.error_on_delete_button
                        )}
                        endIcon={<DeleteForeverIcon />}
                        type="button"
                        variant="outlined"
                        color="error"
                        sx={{width: '100%', maxWidth: '252px'}}
                        onClick={(event: any) => {
                          event.preventDefault();
                          setDeleteDialogOpened(true);
                        }}
                      />
                      <LoadingButton
                        sx={{
                          width: '100%',
                          maxWidth: '252px',
                        }}
                        type="button"
                        variant="contained"
                        loading={saveButtonState === SubmitButtonState.Loading}
                        loadingPosition={
                          saveButtonState === SubmitButtonState.Loading ? 'start' : undefined
                        }
                        disabled={
                          !canWrite ||
                          saveButtonDisabled ||
                          saveButtonState === SubmitButtonState.Loading ||
                          deleteButtonState === SubmitButtonState.Loading
                        }
                        onClick={handleSaveChanges}
                      >
                        {t(translations.entity_services.edit.save_button)}
                      </LoadingButton>
                    </Stack>
                  </Box>
                ) : (
                  <Stack
                    className="entity-service-actions-footer"
                    flexDirection="row"
                    gap="10px"
                    justifyContent="space-between"
                  >
                    <Button
                      variant="text"
                      color="error"
                      type="button"
                      sx={{
                        width: '100%',
                        height: '40px',
                        padding: '8px 16px',
                        borderRadius: '5px',
                        maxWidth: '252px',
                      }}
                      disabled={
                        saveButtonState === SubmitButtonState.Loading ||
                        deleteButtonState === SubmitButtonState.Loading
                      }
                      onClick={handleExit}
                    >
                      {t(translations.office.generics.cancel)}
                    </Button>
                    <LoadingButton
                      sx={{
                        width: '100%',
                        maxWidth: '252px',
                      }}
                      type="button"
                      variant="contained"
                      loading={saveButtonState === SubmitButtonState.Loading}
                      loadingPosition={
                        saveButtonState === SubmitButtonState.Loading ? 'start' : undefined
                      }
                      disabled={
                        !canWrite ||
                        saveButtonDisabled ||
                        saveButtonState === SubmitButtonState.Loading ||
                        deleteButtonState === SubmitButtonState.Loading
                      }
                      onClick={handleSaveChanges}
                    >
                      {t(translations.entity_services.edit.save_button)}
                    </LoadingButton>
                  </Stack>
                )}
              </form>
            ) : (
              <Box className="entity-service-loading-container">
                <LoadingContent />
              </Box>
            )}
            {model && canWrite && (
              <>
                <IconPicker
                  catalog={IconsCatalog.EntityServices}
                  appButtonColors={appColors.appButtons.entityServices}
                  open={iconPickerOpened}
                  iconUrl={model.iconUrl}
                  onClose={(iconUrl?: string) => {
                    // Close picker
                    setIconPickerOpened(false);

                    // Set it
                    setModel({
                      ...model,
                      iconUrl: iconUrl ? iconUrl : '',
                    });
                  }}
                />
                {imageCropperFile && (
                  <ImageCropperDialog
                    open={true}
                    imageFile={imageCropperFile}
                    handleClose={handleImageFileCropperDialogClosed}
                    aspect={appSizes.appButtonSize.width / appSizes.appButtonSize.height}
                    size={`${appSizes.appButtonSize.width}x${appSizes.appButtonSize.height}`}
                  />
                )}
              </>
            )}
            {/* Delete dialog */}
            {model && model.id && canWrite && (
              <Modal
                className="entity-service-modal"
                id="confirm-delete-service"
                open={deleteDialogOpened}
                onClose={() => setDeleteDialogOpened(false)}
                maxWidth="sm"
                fullWidth
                preventCloseClick
                actions={[
                  {
                    label: t(translations.modal.cancel),
                    type: 'destructive',
                    onClick: () => handleDeleteDialogClosed('no'),
                  },
                  {
                    label: t(translations.modal.confirm),
                    type: 'nondestructive',
                    onClick: () => handleDeleteDialogClosed('yes'),
                  },
                ]}
              >
                <Stack>
                  <Typography className="title">
                    {t(translations.entity_services.edit.delete_dialog_title, {
                      name: originalModel?.title,
                    })}
                  </Typography>
                  <Typography className="subtitle">
                    {t(translations.entity_services.edit.delete_dialog_content, {
                      caregiversAppName: appContext.state.currentThemeSettings.caregiversAppName,
                      seniorsDeviceName: appContext.state.currentThemeSettings.seniorsDeviceName,
                    })}
                  </Typography>
                </Stack>
              </Modal>
            )}
            {/* Save dialog */}
            {model && canWrite && (
              <>
                <RouterPrompt
                  // Show prompt if the data changed but make sure the path
                  // is not changing because the service was deleted/created
                  when={
                    dataChanged &&
                    saveButtonState !== SubmitButtonState.Loading &&
                    deleteButtonState !== SubmitButtonState.Loading
                  }
                  subtitle={
                    model.id
                      ? t(translations.entity_services.edit.router_prompt_edit_title, {
                          name: originalModel?.title || '',
                        })
                      : t(translations.entity_services.edit.router_prompt_create_title, {
                          name: model?.title || '',
                        })
                  }
                  message={
                    model.id
                      ? t(translations.entity_services.edit.router_prompt_edit_message)
                      : t(translations.entity_services.edit.router_prompt_create_message)
                  }
                  onOK={() => true}
                  onCancel={() => false}
                  toastNotification={{
                    severity: 'info',
                    message: model.id
                      ? t(translations.entity_services.edit.service_edit_cancel_toast, {
                          name: originalModel?.title || '',
                        })
                      : t(translations.entity_services.edit.service_create_cancel_toast),
                  }}
                />
                <Modal
                  className="entity-service-modal"
                  id="confirm-save-service"
                  open={saveDialogOpened}
                  onClose={() => setSaveDialogOpened(false)}
                  maxWidth="sm"
                  fullWidth
                  preventCloseClick
                  actions={[
                    {
                      label: t(
                        translations.entity_services.edit.save_service_dialog_action_cancel_button
                      ),
                      type: 'destructive',
                      onClick: () => handleSaveDialogClosed('no'),
                    },
                    {
                      label: t(
                        translations.entity_services.edit.save_service_dialog_action_save_button
                      ),
                      type: 'nondestructive',
                      onClick: () => handleSaveDialogClosed('yes'),
                    },
                  ]}
                >
                  <Stack>
                    <Typography className="title">
                      {model.id
                        ? t(translations.entity_services.edit.edit_service_dialog_title, {
                            name: originalModel?.title,
                          })
                        : t(translations.entity_services.edit.create_service_dialog_title, {
                            name: model.title,
                          })}
                    </Typography>
                    <Typography className="subtitle">
                      {model.id
                        ? t(translations.entity_services.edit.edit_service_dialog_message)
                        : t(translations.entity_services.edit.create_service_dialog_message)}
                    </Typography>
                  </Stack>
                </Modal>
              </>
            )}
          </ContentPage>
        </Box>
      </Box>
    </Box>
  );
}
